import { useQuery } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import {
  FundPaymentsConsolidated,
  NomineesAnalyticsResponse,
  NomineesByPeriodAnalytics,
  NomineesSummary,
  OverdueAndDueDatesResponse,
} from './types';

const FUNDS_DASHBOARD_KEYS = ['fundsDashboard'];

export const getNomineesSummary = async (
  dispatch: Dispatch,
  query?: string,
): Promise<NomineesSummary | undefined> => {
  const queryString = query ? `?${query}` : '';
  const response = await requestHandler({
    method: 'get',
    url: `/internal/funds-dashboard/nominees/summary${queryString}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getNomineesSummaryQuery = (dispatch: Dispatch, query?: string) =>
  useQuery({
    queryKey: [...FUNDS_DASHBOARD_KEYS, 'nomineesSummary', query ?? ''],
    queryFn: async (): Promise<NomineesSummary | undefined> => {
      return getNomineesSummary(dispatch, query);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 1,
  });

export const getNomineesAnalytics = async (
  dispatch: Dispatch,
  query?: string,
): Promise<NomineesAnalyticsResponse | undefined> => {
  const queryString = query ? `?${query}` : '';
  const response = await requestHandler({
    method: 'get',
    url: `/internal/funds-dashboard/nominees/analytics${queryString}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getNomineesAnalyticsQuery = (dispatch: Dispatch, query?: string) =>
  useQuery({
    queryKey: [...FUNDS_DASHBOARD_KEYS, 'nomineesAnalytics', query ?? ''],
    queryFn: async (): Promise<NomineesAnalyticsResponse | undefined> => {
      return getNomineesAnalytics(dispatch, query);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 1,
  });

export const getPaymentsConsolidated = async (
  dispatch: Dispatch,
  query?: string,
): Promise<FundPaymentsConsolidated | undefined> => {
  const queryString = query ? `?${query}` : '';
  const response = await requestHandler({
    method: 'get',
    url: `/internal/funds-dashboard/payments/consolidated${queryString}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPaymentsConsolidatedQuery = (
  dispatch: Dispatch,
  query?: string,
) =>
  useQuery({
    queryKey: [...FUNDS_DASHBOARD_KEYS, 'paymentsConsolidated', query ?? ''],
    queryFn: async (): Promise<FundPaymentsConsolidated | undefined> => {
      return getPaymentsConsolidated(dispatch, query);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 1,
  });

export const getOverdueAndDueDates = async (
  dispatch: Dispatch,
  query?: string,
): Promise<OverdueAndDueDatesResponse | undefined> => {
  const queryString = query ? `?${query}` : '';
  const response = await requestHandler({
    method: 'get',
    url: `/internal/funds-dashboard/overdue-and-due-dates${queryString}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getOverdueAndDueDatesQuery = (
  dispatch: Dispatch,
  query?: string,
) =>
  useQuery({
    queryKey: [...FUNDS_DASHBOARD_KEYS, 'overdueAndDueDates', query ?? ''],
    queryFn: async (): Promise<OverdueAndDueDatesResponse | undefined> => {
      return getOverdueAndDueDates(dispatch, query);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 1,
  });

export const getNomineesByPeriodAnalytics = async (
  dispatch: Dispatch,
  query?: string,
): Promise<NomineesByPeriodAnalytics | undefined> => {
  const queryString = query ? `?${query}` : '';
  const response = await requestHandler({
    method: 'get',
    url: `/internal/funds-dashboard/nominees/analytics/period${queryString}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getNomineesByPeriodAnalyticsQuery = (
  dispatch: Dispatch,
  query?: string,
) =>
  useQuery({
    queryKey: [...FUNDS_DASHBOARD_KEYS, 'nomineesByPeriod', query ?? ''],
    queryFn: async (): Promise<NomineesByPeriodAnalytics | undefined> => {
      return getNomineesByPeriodAnalytics(dispatch, query);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 1,
  });
